import type { ReactNode } from 'react';

import { AudioBanner } from '../components/AudioBanner';
import { OutputBar } from '../components/OutputBar';
import { PersistentBar } from '../components/PersistentBar';
import { useUiStore } from './uiStore';

/**
 * The three fixed bands. A surface only ever fills the middle.
 *
 * ```
 * OutputBar      132 pt   in band · centred · tap → Partner-Ansicht
 * [surface]      425 pt   prediction 104 + gap 8 + keyboard 313
 * PersistentBar  178 pt   two rows in band, identical on every surface
 * ```
 *
 * That is the whole point of the frame: the output bar and the bottom bar do not move when the
 * surface changes, so the person's motor memory for `ja`, `Sprechen` and `Notfall` survives
 * navigation. A layout that rearranged itself per screen would defeat the thing this design is
 * most protective of.
 */
export function AppFrame({ children }: { children: ReactNode }): React.JSX.Element {
  const forceTo = useUiStore((s) => s.forceTo);
  const goTo = useUiStore((s) => s.goTo);
  const surface = useUiStore((s) => s.surface);

  /**
   * Where the output bar's tap goes (`SUR-25`, ADR-043).
   *
   * The frame decides rather than the bar, because the frame already owns navigation — and keeping the German
   * inside `OutputBar` means the label and the destination cannot disagree.
   */
  const destination = surface === 'sprechen' ? 'partner' : 'keyboard';

  return (
    <div className="app-shell" data-testid="app-shell">
      <AudioBanner />
      <OutputBar
        destination={destination}
        onActivate={() =>
          destination === 'partner' ? forceTo('partner', null) : goTo('sprechen')
        }
      />
      <main style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {children}
      </main>
      <PersistentBar />
    </div>
  );
}
